import type { ExpressionSpecification } from 'mapbox-gl';
import type { MapboxSourceConfig } from '~/store2/layers';
import { zoomInterp } from './HighlightLayer';
import type { FeatureRef, SidebarSection } from './MetadataSidebar/buildSidebarSections';

// The one feature-state key the highlight stack reads. Namespaced so it can't collide with any
// future state written by another widget on the same source.
export const HIGHLIGHT_FEATURE_STATE_KEY = 'cgHighlight';

// Every CG vector tile is written with a single source-layer named 'all' (tile-server
// `vtpbf.fromGeojsonVt({ all: tile })`), so promoteId only ever needs that one key.
const SOURCE_LAYER = 'all';

// CivilGrid's own per-feature identity, present on the PREBUILT (.mbtiles) datasets.
const CG_FEATURE_ID_FIELD = 'feature_id';

// mapbox `promoteId` for a layer's vector source — the property mapbox copies into `feature.id`,
// which is what setFeatureState addresses.
//
// `config.fields` describes the ESRI SERVICE, and the tiles are not always built from it: a layer
// with a `/tile-data/<id>.mbtiles` is served straight from that prebuilt file (TileServer.getTile),
// bypassing the ESRI query and geojson-vt entirely. Verified for `county` (EXP-5): the config
// declares OBJECTID as its only 'id'-typed field, yet the baked tiles carry every other declared
// field and NOT OBJECTID — so promoting the declared OID yields no ids at all and feature-state
// silently cannot address anything. `feature_id` is present in both, so prefer it when the layer
// offers it, and fall back to the declared OID for dynamically-tiled layers that lack it.
export function resolvePromoteId(config: Pick<MapboxSourceConfig, 'fields'>): { all: string } | undefined {
  const fields = config.fields ?? {};
  if (fields[CG_FEATURE_ID_FIELD]) return { [SOURCE_LAYER]: CG_FEATURE_ID_FIELD };
  const idField = Object.keys(fields).find((k) => fields[k]?.type === 'id');
  return idField ? { [SOURCE_LAYER]: idField } : undefined;
}

// Identity of the vector source a highlight's paint layers are bound to.
//
// react-map-gl only diffs `beforeId`/`layout`/`paint`/`filter`/zoom range on an existing layer
// (mapbox-legacy/components/layer.js `updateLayer`); `source` and `source-layer` are read once, at
// addLayer time. So a layer whose id stays constant while those props change keeps painting the
// PREVIOUS source. Use this as the React `key` on the layer set to force a remount instead.
export function featureStateSourceKey(ref: Pick<FeatureRef, 'sourceId' | 'sourceLayer'>): string {
  return `${ref.sourceId}::${ref.sourceLayer}`;
}

// Whether a geometry takes the feature-state path at all.
//
// POINTS deliberately stay on the tile-clipped GeoJSON path: only a polygon ring or a line is split
// across tile boundaries, so a point gains nothing from feature state — and the feature-state stack
// paints with fill/line layers only, so routing a point through it would draw NOTHING at all.
// The single source of truth for that rule; both selectFeatureStateTarget (which target paints this
// way) and distinctFeatureStateSources (which sources get a layer set) read it.
export function isFeatureStateGeometry(geometry: GeoJSON.Geometry | null | undefined): boolean {
  if (!geometry) return false;
  return geometry.type !== 'Point' && geometry.type !== 'MultiPoint';
}

// One vector source a highlight layer set is mounted against.
export interface FeatureStateSource {
  key: string;
  sourceId: string;
  sourceLayer: string;
}

// Every distinct source the open sidebar could highlight into, in section order.
//
// The layer sets are mounted from THIS, not from the current target, because adding a layer is not
// free: mapbox-gl's Style._updateLayer marks the layer's source cache 'reload', which discards that
// source's off-screen tile cache and re-parses every live tile in the worker — rebuilding buckets
// for every layer on the tile, not just the ones being added. Mounting per-target made that fire
// again on every hover onto a card from a different source, and again on hovering back. Mounting
// per-source pays it once per sidebar open and leaves hover to flip feature state alone, which is a
// draw-time property that adds nothing to the style.
export function distinctFeatureStateSources(sections: SidebarSection[]): FeatureStateSource[] {
  const sources: FeatureStateSource[] = [];
  const seen = new Set<string>();
  for (const section of sections) {
    if (!section.featureRef || !isFeatureStateGeometry(section.geometry)) continue;
    const key = featureStateSourceKey(section.featureRef);
    if (seen.has(key)) continue;
    seen.add(key);
    sources.push({ key, sourceId: section.featureRef.sourceId, sourceLayer: section.featureRef.sourceLayer });
  }
  return sources;
}

// The four layer ids of one source's highlight stack. Derived from the source key rather than fixed
// constants so several stacks can coexist — mapbox layer ids are global to the style.
export function featureStateLayerIds(sourceKey: string): {
  fill: string;
  glow: string;
  casing: string;
  core: string;
} {
  return {
    fill: `feature-state-highlight-fill-${sourceKey}`,
    glow: `feature-state-highlight-glow-${sourceKey}`,
    casing: `feature-state-highlight-casing-${sourceKey}`,
    core: `feature-state-highlight-core-${sourceKey}`,
  };
}

// `active` when the feature is flagged in feature state, `inactive` otherwise.
//
// This must drive a PAINT property. mapbox-gl rejects feature-state in a `filter`
// ('["feature-state"] expression is not supported in a filter') because filters are evaluated when
// a tile is parsed, while feature state is applied at draw time — so a filtered highlight layer is
// never even added to the style.
export function featureStateCase<T>(active: T, inactive: T): ExpressionSpecification {
  return ['case', ['boolean', ['feature-state', HIGHLIGHT_FEATURE_STATE_KEY], false], active, inactive];
}

// Smallest tile buffer any CG polygon tile ships, in CSS px.
//
// geojson-vt clips each tile to [-buffer, extent + buffer], so a fragment's artificial ring edges
// sit one buffer OUTSIDE its tile rather than on the boundary. Measured: 80/4096 tile units on the
// prebuilt county mbtiles (tippecanoe's default), 64/4096 on tiles the TileServer builds. At 512px
// tiles and tile scale 1 the smaller of those is 64 / 4096 * 512 = 8 px.
export const MIN_TILE_BUFFER_PX = 8;

// Widest stroke a polygon highlight may use, measured on the OUTERMOST band of the stack.
//
// mapbox does NOT stencil-clip fill/line draws to tile bounds — the buffer is the only thing hiding
// a fragment's clip edges — so a stroke centred on one leaks (width / 2 - MIN_TILE_BUFFER_PX) px
// back inside the tile and reads as a tile-shaped outline across the highlight (EXP-5). Verified at
// z10 over Alameda County: a 26px glow drew a ~3px line straight down the z10 tile edge, 18px drew
// none. 14 keeps half the stroke at 7px, inside even the 8px buffer, with room for antialiasing.
export const MAX_POLYGON_STROKE_WIDTH = 14;

// One band of the polygon stroke, scaled so the OUTERMOST band lands on MAX_POLYGON_STROKE_WIDTH.
//
// Every band MUST scale by the same factor, taken from the outer band. Clamping each width
// independently (the first cut of this) drove the 26px glow and the 22px casing both to 14: the
// casing paints after the glow at the identical width, so it covered the blue-80 edge completely
// and the polygon highlight lost a band at every zoom. Scaling instead keeps Seth's 3:242
// proportions — thin colored core, dominant white gap, thin blue-80 edge — inside the safe width.
//
// Rounded to 2dp so the emitted style JSON stays readable and the expressions stay comparable.
export function polygonSafeWidth(width: number, outerWidth: number): number {
  const scale = Math.min(1, MAX_POLYGON_STROKE_WIDTH / outerWidth);
  return Math.round(width * scale * 100) / 100;
}

// A zoom-interpolated width/radius; each stop output is feature-state gated so it collapses to 0
// for features that are not highlighted.
//
// `['zoom']` may only be the input of a TOP-LEVEL interpolate/step, so the feature-state `case`
// goes inside each stop output rather than wrapping the interpolate (which mapbox would reject).
// The curve itself is HighlightLayer's `zoomInterp` — the same base and the same [z10, z18] stops
// the GeoJSON stack paints with, shared rather than restated so the two paths cannot drift.
// Endpoints are the same [z10, z18] pairs HighlightLayer uses, so lines and points paint identically
// in both stacks; polygons are scaled down as a set (see polygonSafeWidth).
//
// `outer` is the OUTERMOST band's [z10, z18] pair — LINE_GLOW_WIDTH for every band in the stack,
// including the glow itself. It sets the polygon scale factor, so passing a band its own widths
// would let each band scale independently and collapse the stack again.
export function featureStateZoomInterp(
  [z10, z18]: [number, number],
  [outerZ10, outerZ18]: [number, number],
): ExpressionSpecification {
  return zoomInterp(
    featureStateCase<ExpressionSpecification | number>(geometryCappedWidth(z10, outerZ10), 0),
    featureStateCase<ExpressionSpecification | number>(geometryCappedWidth(z18, outerZ18), 0),
  );
}

// The designed width for lines and points, scaled down for polygons.
//
// Only a polygon fragment carries artificial ring EDGES; a clipped line fragment ends in a cap that
// the neighbouring tile's fragment continues straight through, and points are never clipped. So
// lines and points keep the full designed weight and only polygons pay for the buffer.
function geometryCappedWidth(width: number, outerWidth: number): ExpressionSpecification {
  return ['match', ['geometry-type'], ['Polygon', 'MultiPolygon'], polygonSafeWidth(width, outerWidth), width];
}
